import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dumbbell, Clock, Repeat, Trash2, Music4, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { fmtTime } from "@/lib/music";

export default function Practice() {
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    const [s, st] = await Promise.all([
      api.get("/practice/sessions"),
      api.get("/practice/stats"),
    ]);
    setSessions(s.data);
    setStats(st.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    await api.delete(`/practice/sessions/${id}`);
    load();
  };

  return (
    <div className="space-y-6 sw-fade-up">
      <header>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-white">Practice</h1>
        <p className="mt-1 text-sm text-slate-400">Your learning sessions & progress</p>
      </header>

      {/* stats */}
      <div className="grid grid-cols-3 gap-3">
        <Stat icon={Dumbbell} label="Sessions" value={stats?.total_sessions ?? 0} />
        <Stat icon={Clock} label="Practice time" value={fmtTime(stats?.total_seconds ?? 0)} />
        <Stat icon={Repeat} label="Total reps" value={stats?.total_reps ?? 0} />
      </div>

      <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4">
        <div className="flex items-center gap-2 font-semibold text-white">
          <TrendingUp size={16} className="text-cyan-400" /> Learn a song
        </div>
        <p className="mt-1 text-sm text-slate-400">Open any analyzed song and switch to the Practice tab to slow it down, loop sections and pick your part.</p>
        <button data-testid="go-library-practice" onClick={() => nav("/library")} className="mt-3 rounded-xl bg-cyan-500 px-4 py-2 text-sm font-semibold text-[#0B0E14] hover:bg-cyan-400">
          Go to Library
        </button>
      </div>

      <section>
        <h2 className="mb-3 font-display text-lg font-bold text-white">Recent Sessions</h2>
        {sessions.length ? (
          <div className="grid gap-3">
            {sessions.map((s) => (
              <div key={s.id} data-testid={`practice-session-${s.id}`} className="flex items-center gap-4 rounded-xl border border-[#222A38] bg-[#131822] p-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-400">
                  <Music4 size={20} />
                </div>
                <button onClick={() => nav(`/studio/${s.song_id}?tab=practice`)} className="min-w-0 flex-1 text-left">
                  <div className="truncate font-semibold text-white">{s.song_title}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 font-mono-sw text-[11px] text-slate-500">
                    <span className="text-cyan-400">{s.part}</span>
                    <span>{s.section}</span>
                    <span>{s.speed}%</span>
                    {s.transpose !== 0 && <span>{s.transpose > 0 ? `+${s.transpose}` : s.transpose} semi</span>}
                    <span>{fmtTime(s.duration_seconds)}</span>
                    <span>{s.repetitions} reps</span>
                  </div>
                </button>
                <button data-testid={`delete-session-${s.id}`} onClick={() => del(s.id)} className="rounded-full p-1.5 text-slate-500 hover:text-red-400">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-[#222A38] bg-[#131822]/50 p-8 text-center text-sm text-slate-500">
            No practice sessions yet. Save one from the Studio Practice tab.
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-[#222A38] bg-[#131822] p-4 text-center">
      <Icon size={18} className="mx-auto mb-1.5 text-cyan-400" />
      <div className="font-display text-xl font-extrabold text-white">{value}</div>
      <div className="font-mono-sw text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}
